import type { RemoteInfo } from './runtime';
import { bustUrlCache } from '../helpers/bustUrlCache';
import { getExoSkeletonRemotesUrl } from '../helpers/getExoSkeletonRemotesUrl';
import { promiseWithRetries } from '../helpers/promiseWithRetries';

interface FetchRemotesOptions {
  transformRemotesUrl?: (url: string) => string;
}

const REMOTES_FETCH_TIMEOUT_MS = 19_000;

export async function fetchRemotes({
  transformRemotesUrl,
}: FetchRemotesOptions = {}): Promise<RemoteInfo[]> {
  let remotesUrl = getExoSkeletonRemotesUrl();

  if (transformRemotesUrl) {
    remotesUrl = transformRemotesUrl(remotesUrl);
  }

  // Older iOS Safari still gets the deadline, but cannot abort the request.
  let abortController: AbortController | undefined;
  if (typeof AbortController === 'function') {
    abortController = new AbortController();
  }
  const deadlineError = new Error(
    `Failed to fetch remotes within ${REMOTES_FETCH_TIMEOUT_MS} milliseconds`,
  );
  let hasDeadlineExpired = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    timeoutId = globalThis.setTimeout(() => {
      hasDeadlineExpired = true;
      reject(deadlineError);
      abortController?.abort();
    }, REMOTES_FETCH_TIMEOUT_MS);
  });

  const fetchPromise = promiseWithRetries(
    ({ attempt }) => {
      if (hasDeadlineExpired) {
        throw deadlineError;
      }

      const url = attempt > 1 ? bustUrlCache(remotesUrl) : remotesUrl;
      const request = abortController
        ? fetch(url, { signal: abortController.signal })
        : fetch(url);

      return request.then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch remotes: "${response.statusText}"`);
        }

        return response.json();
      });
    },
    {
      retries: 3,
      // The race has already rejected once the deadline fires; throwing from
      // `onRetry` stops `promiseWithRetries` scheduling another attempt. The
      // flag, not `signal.aborted`, so the no-AbortController path stops too.
      onRetry: () => {
        if (hasDeadlineExpired) {
          throw deadlineError;
        }
      },
    },
  );

  try {
    return await Promise.race([fetchPromise, timeoutPromise]);
  } finally {
    if (timeoutId !== undefined) {
      globalThis.clearTimeout(timeoutId);
    }
  }
}
